import {
  BorderShape,
  BorderType,
  ButtonPosition,
  ButtonState,
  FlexType,
  FontWeight,
  HoverStyle,
  IconType,
  Margin,
  Padding,
  Width,
} from '../types/fitQuizButton/ButtonState';
import { swimwearIcons } from './swimwear.icons';
import { bottomsIcons } from './bottoms.icons';
import { hangerIcons } from './hanger.icons';
import { jacketIcons } from './jacket.icons';
import { shoeIcons } from './shoe.icons';
import { specialIcons } from './special.icons';
import { apparelIcons } from './apparel.icons';

let defaultIcon = {
  iconType: 'Special' as IconType,
  specialIcon: specialIcons[0],
  swimwearIcon: swimwearIcons[0],
  apparelIcon: apparelIcons[0],
  hangerIcon: hangerIcons[0],
  shoeIcon: shoeIcons[0],
  bottomsIcon: bottomsIcons[0],
  jacketIcon: jacketIcons[0],
}

type CTAIcons = typeof defaultIcon & {
  customIconUrl?: string;
};

const extractFontSize = (css: string) => {
  let regex = /font-size:(.\d+)/;
  let match = css.match(regex);
  return match && match.length > 1 ? +match[1] : 16;
};

const extractFontColor = (css: string) => {
  let regex = /[^-]color:(.[#0-9aA-zZ]+)/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1].toString() : '#000000';
};

const extractBackgroundColor = (css: string) => {
  let regex = /background:(.*[#0-9aA-zZ]+)/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1].toString().trim() : 'transparent';
};

const extractFontWeight = (css: string) => {
  let regex = /font-weight:.(\d+)/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1].toString() : '400';
};

const extractBorderProperties = (css: string) => {
  let regex = /border:(.\d+px) ([aA-zZ)]+) ([#aA-zZ0-9)]+)/;
  let match = css.match(regex);
  if (!match) {
    let bottomMatch = css.match(
      /border-bottom:(.\d+px) ([aA-zZ)]+) ([#aA-zZ0-9)]+)/
    );
    return bottomMatch && bottomMatch.length > 3
      ? {
          borderType: 'bottom',
          borderColor: bottomMatch[3].toString(),
          borderShape: bottomMatch[2].toString(),
        }
      : { borderType: 'none', borderColor: '#000000' };
  }
  return match && match.length > 3
    ? {
        borderType: 'all',
        borderColor: match[3].toString(),
        borderShape: match[2].toString(),
      }
    : { borderType: 'none', borderColor: '#000000' };
};

const extractBorderRadius = (css: string) => {
  let regex = /border-radius:(.\d+)px/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1] : 0;
};

//TODO:  Also check if it is the hover underline
const extractUnderline = (css: string) => {
  let regex = /text-decoration:(.underline)/;
  let match = css.match(regex);
  return match && match.length > 1 ? true : false;
};

const extractOnlyCapitalLetters = (css: string) => {
  let regex = /text-transform:(.uppercase)/;
  let match = css.match(regex);
  return match && match.length > 1 ? true : false;
};

const extractPadding = (css: string) => {
  let regex = /padding:(.-?[0-9]+)[aA-zZ]*(.-?[0-9]+)[aA-zZ]*/;
  let match = css.match(regex);
  return match && match.length > 2
    ? { horizontal: +match[2], vertical: +match[1] }
    : { horizontal: 0, vertical: 0 };
};

const extractMargin = (css: string) => {
  let regex = /margin:(.-?[0-9]+)[aA-zZ]*(.-?[0-9]+)[aA-zZ]*(.-?[0-9]+){0,1}/;
  let match = css.match(regex);
  if (!match) {
    return { top: 0, bottom: 0, horizontal: 0 };
  }
  if (match.length < 3) {
    return {
      top: +match[1],
      horizontal: +match[2],
      bottom: +match[1],
    };
  }
  return {
    top: +match[1],
    horizontal: +match[2],
    bottom: +match[3],
  };
};

const extractWidth = (css: string) => {
  let regex = /width:(.[a-z-]+)/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1].toString().trim() : 'auto';
};

const extractJustifyContent = (css: string) => {
  let regex = /justify-content:.([a-z-]*)/;
  let match = css.match(regex);
  return match && match.length > 1 ? match[1].toString().trim() : 'flex-start';
};

const extractIconDetails = (
  css: string
): CTAIcons => {
  
  let customRegex = /background-image: url\((.+)\)/;
  let customMatch = css.match(customRegex);
  if (customMatch && customMatch.length > 1) {
    return {
      ...defaultIcon,
      iconType: 'Custom',
      customIconUrl: customMatch[1],
    };
  } else {
    let regex = /-webkit-mask-image: url\((.+)\)/;
    let match = css.match(regex);
    if (!match) {
      return {
        ...defaultIcon,
        iconType: 'None'
      };
    } else if (match.length > 1) {
      let matchIconType = match[1].toLowerCase();
       if (matchIconType.includes('hanger')) {
        return {
          ...defaultIcon,
          iconType: 'Hanger',
          hangerIcon: { key: match[1], url: match[1] }
        };
      } else if (matchIconType.includes('shoe')) {
        return {
          ...defaultIcon,
          iconType: 'Shoe',
          shoeIcon: { key: match[1], url: match[1] },
        };
      }else if (matchIconType.includes('apparel')) {
        return {
          ...defaultIcon,
          iconType: 'Apparel',
          apparelIcon: { key: match[1], url: match[1] },
        };
      }else if (matchIconType.includes('bottom')) {
        return {
          ...defaultIcon,
          iconType: 'Bottom',
          bottomsIcon: { key: match[1], url: match[1] },
        };
      }else if (matchIconType.includes('swimwear')) {
        return {
          ...defaultIcon,
          iconType: 'Swimwear',
          swimwearIcon: { key: match[1], url: match[1] },
        };
      }else if (matchIconType.includes('jacket')) {
        return {
          ...defaultIcon,
          iconType: 'Jacket',
          jacketIcon: { key: match[1], url: match[1] },
        };
      } else {
        return {
          ...defaultIcon,
          iconType: 'Special',
          specialIcon: { key: match[1], url: match[1] },
        };
      }
    }
  }
  return defaultIcon;
}

const getHoverStyle = (css: string): HoverStyle => {
  if (!css.includes('hover')) {
    return 'None';
  } else if (css.includes('text-decoration')) {
    return 'Underline';
  }
  return 'Background';
};

const getHoverBackground = (
  css: string,
  hoverStyle: HoverStyle
): string | undefined => {
  if (hoverStyle === 'Background') {
    let regex = /background:(.*[#0-9aA-zZ]+)/;
    let match = css.match(regex);
    return match && match.length > 1
      ? match[1].toString().trim()
      : 'transparent';
  }
  return undefined;
};

const getButtonPosition = (css: string): ButtonPosition => {
  let positionRegex = /position\s*:\s*fixed/;
  let positionMatch = css.match(positionRegex);
  if (!positionMatch || positionMatch.length < 1) {
    return 'Default';
  } else {
    let leftMatch = css.match(/left\s*:\s*0/);
    let rightMatch = css.match(/right\s*:\s*0/);

    if (leftMatch && leftMatch.length >= 1) {
      return 'Left';
    } else if (rightMatch && rightMatch.length >= 1) {
      return 'Right';
    } else {
      return 'Bottom';
    }
  }
};

export const mapCssToButtonState = (css: string): ButtonState => {
  const mediaRules = css.split('@');
  const hasMobileCss = mediaRules.length > 1;
  const mobileCss = hasMobileCss ? mediaRules[1] : css;
  const desktopFontSize = extractFontSize(css);
  const mobileFontSize = extractFontSize(mobileCss);
  const fontColor = extractFontColor(css);
  const desktopBackgroundColor = extractBackgroundColor(css);
  const mobileBackgroundColor = extractBackgroundColor(mobileCss);
  const desktopFontWeight = extractFontWeight(css) as FontWeight;
  const mobileFontWeight = extractFontWeight(mobileCss) as FontWeight;
  const desktopBorderProperties = extractBorderProperties(css);
  const mobileBorderProperties = extractBorderProperties(mobileCss);
  const desktopUnderline = extractUnderline(css);
  const mobileUnderline = extractUnderline(mobileCss);
  const desktopOnlyCapitalLetters = extractOnlyCapitalLetters(css);
  const mobileOnlyCapitalLetters = extractOnlyCapitalLetters(mobileCss);
  const desktopPadding = extractPadding(css) as Padding;
  const mobilePadding = extractPadding(mobileCss) as Padding;
  const desktopMargin = extractMargin(css) as Margin;
  const mobileMargin = extractMargin(mobileCss) as Margin;
  const desktopJustifyContent = extractJustifyContent(css) as FlexType;
  const mobileJustifyContent = extractJustifyContent(mobileCss) as FlexType;
  const { iconType, specialIcon, hangerIcon, shoeIcon, apparelIcon, bottomsIcon, jacketIcon, swimwearIcon, customIconUrl } =
    extractIconDetails(css);
  const desktopWidth = extractWidth(css) as Width;
  const mobileWidth = extractWidth(mobileCss) as Width;
  const desktopBorderRadius = extractBorderRadius(css);
  const mobileBorderRadius = extractBorderRadius(mobileCss);
  const hoverStringIndex = css.indexOf('hover');
  const mobileHoverStringIndex = mobileCss.indexOf('hover');
  const desktopHoverCss = css.substring(
    hoverStringIndex,
    css.indexOf('}', hoverStringIndex)
  );
  const mobileHoverCss = mobileCss.substring(
    mobileHoverStringIndex,
    mobileCss.indexOf('}', mobileHoverStringIndex)
  );
  const desktopHoverStyle = getHoverStyle(desktopHoverCss);

  const mobileHoverStyle = getHoverStyle(mobileHoverCss);
  const desktopButtonPosition = getButtonPosition(css) as ButtonPosition;
  const mobileButtonPosition = getButtonPosition(mobileCss) as ButtonPosition;

  const desktopAdvancedSettings = {
    backgroundColor: desktopBackgroundColor,
    fontSize: desktopFontSize,
    fontWeight: desktopFontWeight,
    borderType: desktopBorderProperties.borderType as BorderType,
    borderRadius: +desktopBorderRadius,
    borderColor: desktopBorderProperties.borderColor,
    borderShape:
      (desktopBorderProperties.borderShape as BorderShape) ?? 'solid',
    underline: desktopUnderline,
    onlyCapitalLetters: desktopOnlyCapitalLetters,
    padding: desktopPadding,
    margin: desktopMargin,
    width: desktopWidth,
    justifyContent: desktopJustifyContent,
    hoverStyle: desktopHoverStyle,
    hoverBackgroundColor: getHoverBackground(
      desktopHoverCss,
      desktopHoverStyle
    ),
    buttonPosition: desktopButtonPosition,
  };

  const mobileAdvancedSettings = {
    backgroundColor: mobileBackgroundColor,
    fontSize: mobileFontSize,
    fontWeight: mobileFontWeight,
    borderType: mobileBorderProperties.borderType as BorderType,
    borderColor: mobileBorderProperties.borderColor,
    borderRadius: +mobileBorderRadius,
    borderShape: (mobileBorderProperties.borderShape as BorderShape) ?? 'solid',
    underline: mobileUnderline,
    onlyCapitalLetters: mobileOnlyCapitalLetters,
    padding: mobilePadding,
    margin: mobileMargin,
    width: mobileWidth,
    justifyContent: mobileJustifyContent,
    hoverStyle: mobileHoverStyle,
    hoverBackgroundColor: getHoverBackground(mobileHoverCss, mobileHoverStyle),
    buttonPosition: mobileButtonPosition,
  };

  return {
    fontColor,
    iconType,
    specialIcon,
    hangerIcon,
    shoeIcon,
    apparelIcon,
    bottomsIcon,
    jacketIcon,
    swimwearIcon,
    customIconUrl,
    desktopAdvancedSettings,
    mobileAdvancedSettings,
    desktopAndButtonStyleSame:
      desktopAdvancedSettings === mobileAdvancedSettings,
  };
};
